import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, ChevronRight, ChevronDown, Users, Target, FileText, CheckCircle, Activity, Search, X } from 'lucide-react';
// === TYPES ===
type TimePeriod = 'Daily' | 'Weekly' | 'Monthly' | 'Custom';
type MetricCategory = 'creditPulls' | 'readyToPitch' | 'docsOut' | 'subs';
interface LoanRecord {
  id: string;
  borrowerName: string;
  loanNumber: string;
  propertyAddress: string;
  loanAmount: number;
  date: string;
  status: string;
}
interface Metrics {
  creditPulls: number;
  readyToPitch: number;
  docsOut: number;
  subs: number;
}
interface LOA {
  id: string;
  name: string;
  metrics: Record<TimePeriod, Metrics>;
  records: Record<MetricCategory, LoanRecord[]>;
}
interface Team {
  id: string;
  name: string;
  lead: string;
  loas: LOA[];
}
interface Division {
  id: string;
  name: string;
  teams: Team[];
}
// === MOCK DATA GENERATORS ===
const BORROWER_NAMES = ['John Smith', 'Emily Johnson', 'Michael Brown', 'Sarah Davis', 'David Wilson', 'Jennifer Martinez', 'Robert Anderson', 'Lisa Taylor', 'James Thomas', 'Mary Jackson', 'Christopher White', 'Patricia Harris', 'Daniel Martin', 'Barbara Thompson', 'Matthew Garcia', 'Susan Martinez', 'Joseph Robinson', 'Nancy Clark', 'Charles Rodriguez', 'Karen Lewis'];
const STREET_NAMES = ['Oak St', 'Maple Ave', 'Pine Rd', 'Cedar Ln', 'Elm Dr', 'Birch Way', 'Willow Ct', 'Spruce Blvd', 'Ash Pl', 'Cherry Ter'];
const CITIES = ['Austin', 'Denver', 'Seattle', 'Portland', 'Phoenix', 'San Diego', 'Nashville', 'Charlotte', 'Raleigh', 'Tampa'];
const STATUSES = ['In Progress', 'Pending Review', 'Complete', 'On Hold'];
const generateLoanRecord = (index: number): LoanRecord => {
  const borrower = BORROWER_NAMES[Math.floor(Math.random() * BORROWER_NAMES.length)];
  const street = `${Math.floor(Math.random() * 9999) + 1000} ${STREET_NAMES[Math.floor(Math.random() * STREET_NAMES.length)]}`;
  const city = CITIES[Math.floor(Math.random() * CITIES.length)];
  const amount = Math.floor(Math.random() * 500000) + 200000;
  const daysAgo = Math.floor(Math.random() * 30);
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return {
    id: `loan-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
    borrowerName: borrower,
    loanNumber: `LN${Math.floor(Math.random() * 900000) + 100000}`,
    propertyAddress: `${street}, ${city}`,
    loanAmount: amount,
    date: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    status: STATUSES[Math.floor(Math.random() * STATUSES.length)]
  };
};
const generateMetrics = (baseMultiplier: number): Record<TimePeriod, Metrics> => {
  const cpDaily = Math.floor(Math.random() * 8) + baseMultiplier + 3;
  const rtpDaily = Math.floor(cpDaily * (Math.random() * 0.4 + 0.4));
  const docsDaily = Math.floor(rtpDaily * (Math.random() * 0.5 + 0.3));
  const subsDaily = Math.floor(docsDaily * (Math.random() * 0.6 + 0.4));
  return {
    Daily: {
      creditPulls: cpDaily,
      readyToPitch: rtpDaily,
      docsOut: docsDaily,
      subs: subsDaily
    },
    Weekly: {
      creditPulls: cpDaily * 5,
      readyToPitch: rtpDaily * 5,
      docsOut: docsDaily * 5,
      subs: subsDaily * 5
    },
    Monthly: {
      creditPulls: cpDaily * 20,
      readyToPitch: rtpDaily * 20,
      docsOut: docsDaily * 20,
      subs: subsDaily * 20
    }
  };
};
const generateLoanRecords = (metrics: Record<TimePeriod, Metrics>): Record<MetricCategory, LoanRecord[]> => {
  // Use Monthly counts for record generation (most comprehensive)
  const counts = metrics.Monthly;
  return {
    creditPulls: Array.from({
      length: counts.creditPulls
    }, (_, i) => generateLoanRecord(i)),
    readyToPitch: Array.from({
      length: counts.readyToPitch
    }, (_, i) => generateLoanRecord(i)),
    docsOut: Array.from({
      length: counts.docsOut
    }, (_, i) => generateLoanRecord(i)),
    subs: Array.from({
      length: counts.subs
    }, (_, i) => generateLoanRecord(i))
  };
};
const createLOA = (id: string, name: string, baseMultiplier: number): LOA => {
  const metrics = generateMetrics(baseMultiplier);
  const records = generateLoanRecords(metrics);
  return {
    id,
    name,
    metrics,
    records
  };
};
const MOCK_DATA: Division[] = [{
  id: 'div-paul',
  name: "Paul's Division",
  teams: [{
    id: 'team-alex',
    name: 'Team Alex',
    lead: 'Alex Mannhalt',
    loas: [createLOA('loa-1', 'Sarah Jenkins', 2), createLOA('loa-2', 'Mike Ross', 1), createLOA('loa-3', 'Jessica Pearson', 3), createLOA('loa-4', 'Harvey Specter', 4)]
  }, {
    id: 'team-leo',
    name: 'Team Leo',
    lead: 'Leonard Roberts',
    loas: [createLOA('loa-5', 'Louis Litt', 3), createLOA('loa-6', 'Donna Paulsen', 2), createLOA('loa-7', 'Katrina Bennett', 4), createLOA('loa-8', 'Rachel Zane', 1), createLOA('loa-9', 'Robert Zane', 2)]
  }]
}, {
  id: 'div-maria',
  name: "Maria's Division",
  teams: [{
    id: 'team-max',
    name: 'Team Max',
    lead: 'Max Fazio',
    loas: [createLOA('loa-10', 'Jim Halpert', 2), createLOA('loa-11', 'Pam Beesly', 3), createLOA('loa-12', 'Dwight Schrute', 4), createLOA('loa-13', 'Angela Martin', 1)]
  }, {
    id: 'team-kristen',
    name: 'Team Kristen',
    lead: 'Kristen Denha',
    loas: [createLOA('loa-14', 'Stanley Hudson', 3), createLOA('loa-15', 'Phyllis Vance', 2), createLOA('loa-16', 'Oscar Martinez', 2), createLOA('loa-17', 'Kevin Malone', 3)]
  }]
}, {
  id: 'div-james',
  name: "James's Division",
  teams: [{
    id: 'team-sulvin',
    name: 'Team Sulvin',
    lead: 'Sulvin Youanis',
    loas: [createLOA('loa-18', 'Michael Scott', 4), createLOA('loa-19', 'Andy Bernard', 3), createLOA('loa-20', 'Kelly Kapoor', 2), createLOA('loa-21', 'Ryan Howard', 1), createLOA('loa-22', 'Toby Flenderson', 1)]
  }]
}];
// === HELPER FUNCTIONS ===
const calculatePullThrough = (docsOut: number, readyToPitch: number): number => {
  if (readyToPitch === 0) return 0;
  return Math.round(docsOut / readyToPitch * 100);
};
const getPullThroughColor = (rate: number): string => {
  if (rate >= 70) return 'text-emerald-400';
  if (rate >= 40) return 'text-amber-400';
  return 'text-red-400';
};
const getPullThroughGlow = (rate: number): string => {
  if (rate >= 70) return 'value-glow-emerald';
  if (rate >= 40) return 'value-glow-amber';
  return 'text-red-400 drop-shadow-[0_0_8px_rgba(248,113,113,0.6)]';
};
const getMetricLabel = (category: MetricCategory): string => {
  const labels: Record<MetricCategory, string> = {
    creditPulls: 'Soft Pulls',
    readyToPitch: 'Ready to Pitch',
    docsOut: 'Docs Out',
    subs: 'Submitted to UW'
  };
  return labels[category];
};
function getMetricsForPeriod(metrics: Record<'Daily' | 'Weekly' | 'Monthly', Metrics>, period: TimePeriod, customDays: number): Metrics {
  if (period === 'Custom') {
    const daily = metrics.Daily;
    return {
      creditPulls: daily.creditPulls * customDays,
      readyToPitch: daily.readyToPitch * customDays,
      docsOut: daily.docsOut * customDays,
      subs: daily.subs * customDays
    };
  }
  return metrics[period];
}
// === COMPONENTS ===
function KpiCard({
  label,
  value,
  icon: Icon,
  index,
  colorClass
}: {
  label: string;
  value: string | number;
  icon: any;
  index: number;
  colorClass: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 15
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.08,
    duration: 0.4,
    ease: 'easeOut'
  }} className="flex-1 min-w-[200px] glass-card rounded-xl p-5 flex items-center gap-4 cursor-default shimmer-on-hover hover-lift" data-id="element-1009">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 icon-glow-pulse bg-navy-800/50 border border-white/10`} data-id="element-1010">
        <Icon size={20} className={colorClass.split(' ')[0]} data-id="element-1011" />
      </div>
      <div data-id="element-1012">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1" data-id="element-1013">
          {label}
        </p>
        <p className={`text-2xl font-bold ${colorClass}`} data-id="element-1014">{value}</p>
      </div>
    </motion.div>;
}
function DrillDownPanel({
  loaName,
  category,
  records,
  onClose
}: {
  loaName: string;
  category: MetricCategory;
  records: LoanRecord[];
  onClose: () => void;
}) {
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-1015" />

      {/* Panel */}
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed right-0 top-0 bottom-0 w-full max-w-4xl bg-navy-900 border-l border-white/10 z-50 overflow-hidden flex flex-col" data-id="element-1016">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 bg-gradient-to-r from-navy-800/80 to-navy-900/80" data-id="element-1017">
          <div data-id="element-1018">
            <h2 className="text-2xl font-bold gradient-text" data-id="element-1019">{loaName}</h2>
            <p className="text-sm text-slate-400 mt-1" data-id="element-1020">
              {getMetricLabel(category)} — {records.length} Records
            </p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors" data-id="element-1021">
            <X size={20} className="text-slate-400" data-id="element-1022" />
          </button>
        </div>

        {/* Records Table */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1023">
          <div className="glass-card rounded-xl overflow-hidden border border-white/10" data-id="element-1024">
            {/* Table Header */}
            <div className="grid grid-cols-[1.8fr_1fr_2.2fr_1.1fr_1.1fr_1.2fr] gap-3 px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-white/10 bg-navy-900/40" data-id="element-1025">
              <div data-id="element-1026">Borrower</div>
              <div data-id="element-1027">Loan #</div>
              <div data-id="element-1028">Property</div>
              <div className="text-right" data-id="element-1029">Amount</div>
              <div data-id="element-1030">Date</div>
              <div className="text-center" data-id="element-1031">Status</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-white/5" data-id="element-1032">
              {records.map((record, index) => <motion.div key={record.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.03,
              duration: 0.3
            }} className="grid grid-cols-[1.8fr_1fr_2.2fr_1.1fr_1.1fr_1.2fr] gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors" data-id="element-1033">
                  <div className="text-sm font-medium text-slate-200" data-id="element-1034">
                    {record.borrowerName}
                  </div>
                  <div className="text-sm text-slate-400 font-mono" data-id="element-1035">
                    {record.loanNumber}
                  </div>
                  <div className="text-sm text-slate-400" data-id="element-1036">
                    {record.propertyAddress}
                  </div>
                  <div className="text-sm text-emerald-400 font-semibold text-right" data-id="element-1037">
                    ${record.loanAmount.toLocaleString()}
                  </div>
                  <div className="text-sm text-slate-400 whitespace-nowrap" data-id="element-1038">
                    {record.date}
                  </div>
                  <div className="text-center" data-id="element-1039">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${record.status === 'Complete' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : record.status === 'In Progress' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : record.status === 'Pending Review' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`} data-id="element-1040">
                      {record.status}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </>;
}
function MetricsRow({
  name,
  metrics,
  isHeader = false,
  isTeam = false,
  isDivision = false,
  onClick,
  isExpanded,
  onMetricClick
}: {
  name: React.ReactNode;
  metrics?: Metrics;
  isHeader?: boolean;
  isTeam?: boolean;
  isDivision?: boolean;
  onClick?: () => void;
  isExpanded?: boolean;
  onMetricClick?: (category: MetricCategory) => void;
}) {
  if (isHeader) {
    return <div className="grid grid-cols-[2.2fr_1fr_1fr_0.8fr_1fr_1fr_0.8fr] gap-3 px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-white/10 bg-navy-900/40" data-id="element-1041">
        <div data-id="element-1042">Name</div>
        <div className="text-center" data-id="element-1043">Soft Pulls</div>
        <div className="text-center" data-id="element-1044">Ready to Pitch</div>
        <div className="text-center" data-id="element-1045">SP→RTP %</div>
        <div className="text-center" data-id="element-1046">Docs Out</div>
        <div className="text-center" data-id="element-1047">Subs</div>
        <div className="text-center" data-id="element-1048">Docs→Subs %</div>
      </div>;
  }
  if (!metrics) return null;
  const spToRtpRate = metrics.creditPulls === 0 ? 0 : Math.round(metrics.readyToPitch / metrics.creditPulls * 100);
  const docsToSubsRate = metrics.docsOut === 0 ? 0 : Math.round(metrics.subs / metrics.docsOut * 100);
  const spToRtpColor = getPullThroughColor(spToRtpRate);
  const docsToSubsColor = getPullThroughColor(docsToSubsRate);
  const baseClasses = 'grid grid-cols-[2.2fr_1fr_1fr_0.8fr_1fr_1fr_0.8fr] gap-3 px-4 py-3 items-center border-b border-white/5 transition-colors';
  let rowClasses = baseClasses;
  if (isDivision) {
    rowClasses += ' bg-gradient-to-r from-navy-800/80 to-navy-900/80 hover:from-navy-700/80 hover:to-navy-800/80 cursor-pointer';
  } else if (isTeam) {
    rowClasses += ' bg-navy-800/40 hover:bg-navy-700/40 cursor-pointer';
  } else {
    rowClasses += ' bg-transparent hover:bg-white/[0.02]';
  }
  const isClickable = !isDivision && !isTeam && onMetricClick;
  const metricCellClass = `text-center font-semibold ${isDivision ? 'text-white' : 'text-slate-300'} ${isClickable ? 'cursor-pointer hover:text-accent-400 hover:underline transition-colors' : ''}`;
  return <div className={rowClasses} data-id="element-1049">
      <div className="flex items-center gap-2" onClick={onClick} data-id="element-1050">
        {(isDivision || isTeam) && <motion.div animate={{
        rotate: isExpanded ? 90 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-1051">
            <ChevronRight size={16} className="text-slate-400" data-id="element-1052" />
          </motion.div>}
        {!isDivision && !isTeam && <div className="w-4" data-id="element-1053" />}
        <span className={`font-medium ${isDivision ? 'text-white text-base' : isTeam ? 'text-slate-200 text-sm' : 'text-slate-400 text-sm'}`} data-id="element-1054">
          {name}
        </span>
      </div>
      <div className={metricCellClass} onClick={isClickable ? e => {
      e.stopPropagation();
      onMetricClick('creditPulls');
    } : undefined} data-id="element-1055">
        {metrics.creditPulls}
      </div>
      <div className={metricCellClass} onClick={isClickable ? e => {
      e.stopPropagation();
      onMetricClick('readyToPitch');
    } : undefined} data-id="element-1056">
        {metrics.readyToPitch}
      </div>
      <div className={`text-center font-bold ${spToRtpColor}`} data-id="element-1057">
        {spToRtpRate}%
      </div>
      <div className={metricCellClass} onClick={isClickable ? e => {
      e.stopPropagation();
      onMetricClick('docsOut');
    } : undefined} data-id="element-1058">
        {metrics.docsOut}
      </div>
      <div className={metricCellClass} onClick={isClickable ? e => {
      e.stopPropagation();
      onMetricClick('subs');
    } : undefined} data-id="element-1059">
        {metrics.subs}
      </div>
      <div className={`text-center font-bold ${docsToSubsColor}`} data-id="element-1060">
        {docsToSubsRate}%
      </div>
    </div>;
}
// === MAIN COMPONENT ===
export function PullThroughView() {
  const [period, setPeriod] = useState<TimePeriod>('Daily');
  const [selectedDivision, setSelectedDivision] = useState<string>('all');
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set(['div-paul']));
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set(['team-alex']));
  const [drillDown, setDrillDown] = useState<{
    loaName: string;
    category: MetricCategory;
    records: LoanRecord[];
  } | null>(null);
  // Custom date range
  const today = new Date();
  const defaultStart = new Date(today);
  defaultStart.setDate(today.getDate() - 14);
  const [customStart, setCustomStart] = useState(defaultStart.toISOString().split('T')[0]);
  const [customEnd, setCustomEnd] = useState(today.toISOString().split('T')[0]);
  const customDays = useMemo(() => {
    const start = new Date(customStart);
    const end = new Date(customEnd);
    const diff = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    return diff;
  }, [customStart, customEnd]);
  const toggleDivision = (id: string) => {
    setExpandedDivisions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleTeam = (id: string) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  // Filter divisions based on selection
  const filteredDivisions = useMemo(() => {
    if (selectedDivision === 'all') return MOCK_DATA;
    return MOCK_DATA.filter(d => d.id === selectedDivision);
  }, [selectedDivision]);
  // Auto-expand when selecting a specific division
  const handleDivisionSelect = (divId: string) => {
    setSelectedDivision(divId);
    if (divId !== 'all') {
      setExpandedDivisions(new Set([divId]));
      // Expand all teams in the selected division
      const div = MOCK_DATA.find(d => d.id === divId);
      if (div) {
        setExpandedTeams(new Set(div.teams.map(t => t.id)));
      }
    }
  };
  // Calculate totals based on filtered divisions
  const totals = useMemo(() => {
    const t = {
      creditPulls: 0,
      readyToPitch: 0,
      docsOut: 0,
      subs: 0
    };
    filteredDivisions.forEach(div => {
      div.teams.forEach(team => {
        team.loas.forEach(loa => {
          const m = getMetricsForPeriod(loa.metrics, period, customDays);
          t.creditPulls += m.creditPulls;
          t.readyToPitch += m.readyToPitch;
          t.docsOut += m.docsOut;
          t.subs += m.subs;
        });
      });
    });
    return t;
  }, [period, filteredDivisions, customDays]);
  const overallPtRate = calculatePullThrough(totals.docsOut, totals.readyToPitch);
  const divisionOptions = [{
    id: 'all',
    label: 'All Divisions'
  }, ...MOCK_DATA.map(d => ({
    id: d.id,
    label: d.name
  }))];
  const handleMetricClick = (loa: LOA, category: MetricCategory) => {
    setDrillDown({
      loaName: loa.name,
      category,
      records: loa.records[category]
    });
  };
  return <div className="max-w-[1600px] mx-auto p-6 space-y-6" data-id="element-1061">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-1062">
        <div data-id="element-1063">
          <h1 className="text-3xl font-extrabold gradient-text mb-1" data-id="element-1064">
            Metrics Tracker
          </h1>
          <p className="text-sm text-slate-400" data-id="element-1065">
            Identify conversion gaps from pitch to submission
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-1066">
          <div className="flex p-1 bg-navy-900/80 backdrop-blur-md rounded-lg border border-white/10" data-id="element-1067">
            {(['Daily', 'Weekly', 'Monthly', 'Custom'] as TimePeriod[]).map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-5 py-2 text-sm font-semibold rounded-md transition-all ${period === p ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30 shadow-[0_0_15px_rgba(56,189,248,0.15)]' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'}`} data-id="element-1068">
                  {p}
                </button>)}
          </div>

          {/* Custom Date Range Inputs */}
          <AnimatePresence data-id="element-1069">
            {period === 'Custom' && <motion.div initial={{
            opacity: 0,
            width: 0
          }} animate={{
            opacity: 1,
            width: 'auto'
          }} exit={{
            opacity: 0,
            width: 0
          }} transition={{
            duration: 0.25
          }} className="flex items-center gap-2 overflow-hidden" data-id="element-1070">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="px-3 py-2 text-sm bg-navy-900/80 backdrop-blur-md border border-white/10 rounded-lg text-slate-200 focus:outline-none focus:border-accent-500/50 focus:shadow-[0_0_10px_rgba(56,189,248,0.1)] [color-scheme:dark]" data-id="element-1071" />
                <span className="text-slate-500 text-sm" data-id="element-1072">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="px-3 py-2 text-sm bg-navy-900/80 backdrop-blur-md border border-white/10 rounded-lg text-slate-200 focus:outline-none focus:border-accent-500/50 focus:shadow-[0_0_10px_rgba(56,189,248,0.1)] [color-scheme:dark]" data-id="element-1073" />
                <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-1074">
                  ({customDays}d)
                </span>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Division Toggle */}
      <div className="flex flex-wrap gap-2" data-id="element-1075">
        <div className="flex p-1 bg-navy-900/80 backdrop-blur-md rounded-lg border border-white/10 overflow-x-auto" data-id="element-1076">
          {divisionOptions.map(opt => <button key={opt.id} onClick={() => handleDivisionSelect(opt.id)} className={`px-5 py-2 text-sm font-semibold rounded-md transition-all whitespace-nowrap ${selectedDivision === opt.id ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.15)]' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'}`} data-id="element-1077">
              {opt.label}
            </button>)}
        </div>
      </div>

      {/* KPI Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1078">
        <KpiCard index={0} label="Soft Pulls" value={totals.creditPulls} icon={Search} colorClass="value-glow-amber" data-id="element-1079" />
        <KpiCard index={1} label="Ready to Pitch" value={totals.readyToPitch} icon={Target} colorClass="value-glow-cyan" data-id="element-1080" />
        <KpiCard index={2} label="SP → RTP %" value={`${totals.creditPulls === 0 ? 0 : Math.round(totals.readyToPitch / totals.creditPulls * 100)}%`} icon={TrendingUp} colorClass={getPullThroughGlow(totals.creditPulls === 0 ? 0 : Math.round(totals.readyToPitch / totals.creditPulls * 100))} data-id="element-1081" />
        <KpiCard index={3} label="Docs Out" value={totals.docsOut} icon={FileText} colorClass="value-glow-emerald" data-id="element-1082" />
        <KpiCard index={4} label="Submitted to UW" value={totals.subs} icon={CheckCircle} colorClass="value-glow-purple" data-id="element-1083" />
        <KpiCard index={5} label="Docs → Subs %" value={`${totals.docsOut === 0 ? 0 : Math.round(totals.subs / totals.docsOut * 100)}%`} icon={Activity} colorClass={getPullThroughGlow(totals.docsOut === 0 ? 0 : Math.round(totals.subs / totals.docsOut * 100))} data-id="element-1084" />
      </div>

      {/* Data Table Area */}
      <div className="glass-card rounded-xl overflow-hidden border border-white/10" data-id="element-1085">
        <MetricsRow name="" isHeader data-id="element-1086" />

        <div className="overflow-y-auto max-h-[600px] scrollbar-thin" data-id="element-1087">
          {filteredDivisions.map(division => {
          const divMetrics = {
            creditPulls: 0,
            readyToPitch: 0,
            docsOut: 0,
            subs: 0
          };
          division.teams.forEach(team => {
            team.loas.forEach(loa => {
              const m = getMetricsForPeriod(loa.metrics, period, customDays);
              divMetrics.creditPulls += m.creditPulls;
              divMetrics.readyToPitch += m.readyToPitch;
              divMetrics.docsOut += m.docsOut;
              divMetrics.subs += m.subs;
            });
          });
          const isDivExpanded = expandedDivisions.has(division.id);
          return <div key={division.id} data-id="element-1088">
                <MetricsRow name={division.name} metrics={divMetrics} isDivision isExpanded={isDivExpanded} onClick={() => toggleDivision(division.id)} data-id="element-1089" />

                <AnimatePresence data-id="element-1090">
                  {isDivExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-1091">
                      {division.teams.map(team => {
                  const teamMetrics = {
                    creditPulls: 0,
                    readyToPitch: 0,
                    docsOut: 0,
                    subs: 0
                  };
                  team.loas.forEach(loa => {
                    const m = getMetricsForPeriod(loa.metrics, period, customDays);
                    teamMetrics.creditPulls += m.creditPulls;
                    teamMetrics.readyToPitch += m.readyToPitch;
                    teamMetrics.docsOut += m.docsOut;
                    teamMetrics.subs += m.subs;
                  });
                  const isTeamExpanded = expandedTeams.has(team.id);
                  return <div key={team.id} data-id="element-1092">
                            <MetricsRow name={<div className="flex items-center gap-2" data-id="element-1094">
                                  <Users size={14} className="text-accent-400" data-id="element-1095" />
                                  <span data-id="element-1096">{team.name}</span>
                                  <span className="text-[10px] text-slate-500 px-2 py-0.5 rounded-full bg-navy-900/50 border border-white/5" data-id="element-1097">
                                    Lead: {team.lead}
                                  </span>
                                </div>} metrics={teamMetrics} isTeam isExpanded={isTeamExpanded} onClick={() => toggleTeam(team.id)} data-id="element-1093" />

                            <AnimatePresence data-id="element-1098">
                              {isTeamExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden bg-navy-900/20" data-id="element-1099">
                                  {team.loas.map(loa => <MetricsRow key={loa.id} name={loa.name} metrics={getMetricsForPeriod(loa.metrics, period, customDays)} onMetricClick={category => handleMetricClick(loa, category)} data-id="element-1100" />)}
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Drill Down Panel */}
      <AnimatePresence data-id="element-1101">
        {drillDown && <DrillDownPanel loaName={drillDown.loaName} category={drillDown.category} records={drillDown.records} onClose={() => setDrillDown(null)} data-id="element-1102" />}
      </AnimatePresence>
    </div>;
}